import React, { useEffect, useState } from "react";

import { Intercom } from "@intercom/messenger-js-sdk";
import moment from "moment";

import { CfLoader } from "@/shared/components/common/CfLoader/CfLoader";

import { useUser } from "./app.bootstrap";
import { GoogleTagManagerService } from "./shared/services/GoogleTagManager.service";
import { JwtService } from "./shared/services/Jwt.service";
import Localization from "./shared/services/Localization.service";
import { initializeMixpanel } from "./shared/services/mixpanel";
import { logger } from "./shared/services/SentryLogger.service";

import { type ResolvedProps } from "./app.types";

const InitializeIntercom = ({
  children,
  currentFarm,
  farms,
}: React.PropsWithChildren<{
  currentFarm: ResolvedProps["farm"];
  farms: ResolvedProps["farms"];
}>) => {
  const { email, firstName, id, lastName } = useUser();

  useEffect(() => {
    if (!process.env.INTERCOM_APP_ID) return;
    if (JwtService.isImpersonatedToken()) return;
    if (!email) return;

    Intercom({
      hide_default_launcher: true,
      app_id: process.env.INTERCOM_APP_ID,
      company: currentFarm,
      companies: farms,
      email,
      name: `${firstName} ${lastName}`,
      user_id: id,
    });
  }, [currentFarm, email, farms, firstName, id, lastName]);

  return <>{children}</>;
};

const InitializeSentry = ({ children }: React.PropsWithChildren<unknown>) => {
  const { email, firstName, id, lastName } = useUser();

  useEffect(() => {
    if (email) {
      logger.setUser({
        email,
        id,
        username: `${firstName} ${lastName}`,
      });
    } else {
      logger.setUser(null);
    }
  }, [email, firstName, id, lastName]);

  return <>{children}</>;
};

const InitializeLanguage = ({ children }: React.PropsWithChildren<unknown>) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    Localization.initLangInLocalStorage();
    moment.locale(Localization.getLang());
    setReady(true);
  }, []);

  return ready ? <>{children}</> : <CfLoader size={20} />;
};

const InitializeGoogleTagManager = ({
  children,
}: React.PropsWithChildren<unknown>) => {
  const user = useUser();

  useEffect(() => {
    if (user.email) {
      GoogleTagManagerService.initialize(user);
    }
  }, [user]);

  return <>{children}</>;
};

const InitializeMixpanel = ({
  children,
}: React.PropsWithChildren<unknown>): React.ReactElement => {
  const { email, firstName, id, lastName } = useUser();

  useEffect(() => {
    if (email && id) {
      initializeMixpanel({
        id,
        email,
        firstName: firstName ?? "",
        lastName: lastName ?? "",
      });
    }
  }, [email, firstName, id, lastName]);

  return <>{children}</>;
};

export {
  InitializeGoogleTagManager,
  InitializeIntercom,
  InitializeLanguage,
  InitializeMixpanel,
  InitializeSentry,
};
